"use client";

import { useEffect, useState } from "react";
import { useFocusMode } from "@/components/focus/useFocusState";
import { FOCUS_THEME_EVENT, getFocusTheme, getSavedFocusThemeId } from "@/lib/focus-themes";

/**
 * Na czas sesji skupienia przełącza panel na spokojny motyw kolorystyczny wybrany
 * przez ucznia i włącza wygaszanie rozpraszaczy. Ustawia atrybuty + zmienne CSS
 * na <html>; CSS reaguje na `[data-focus-mode="on"]` (patrz globals.css).
 * Używa useFocusMode (prymityw) — brak re-renderów co sekundę.
 */
export default function FocusThemeController() {
  const mode = useFocusMode();
  const active = mode !== "idle";
  const [themeId, setThemeId] = useState<string>(() => getSavedFocusThemeId());

  useEffect(() => {
    const onChange = () => setThemeId(getSavedFocusThemeId());
    window.addEventListener(FOCUS_THEME_EVENT, onChange);
    return () => window.removeEventListener(FOCUS_THEME_EVENT, onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (!active) {
      root.removeAttribute("data-focus-mode");
      root.removeAttribute("data-focus-theme");
      root.removeAttribute("data-focus-dark");
      return;
    }
    const theme = getFocusTheme(themeId);
    const v = theme.vars;
    root.dataset.focusMode = "on";
    root.dataset.focusTheme = theme.id;
    root.dataset.focusDark = String(theme.dark);
    root.style.setProperty("--focus-bg", v.bg);
    root.style.setProperty("--focus-surface", v.surface);
    root.style.setProperty("--focus-surface-2", v.surface2);
    root.style.setProperty("--focus-border", v.border);
    root.style.setProperty("--focus-text", v.text);
    root.style.setProperty("--focus-muted", v.muted);
    root.style.setProperty("--focus-accent", v.accent);
    root.style.setProperty("--focus-accent-dark", v.accentDark);
    root.style.setProperty("--focus-accent-soft", v.accentSoft);
    root.style.setProperty("--focus-saturate", v.saturate);
    return () => {
      root.removeAttribute("data-focus-mode");
      root.removeAttribute("data-focus-theme");
      root.removeAttribute("data-focus-dark");
    };
  }, [active, themeId]);

  return null;
}
